'use client'

import { useEffect, useRef, useState } from 'react'
import { usePrerenderSafeSearchParams } from '@/hooks/use-prerender-safe-search-params'
import { BreadcrumbComponent } from '@/components/ui/breadcrumb'
import { SoilTestForm, type SoilTestSubmissionResult } from '@/components/tools/soil-test-form'
import { SoilTestResults } from '@/components/tools/soil-test-results'
import { useAuth } from '@/contexts/AuthContext'
import { useNewsletterPrompt } from '@/hooks/useNewsletterPrompt'
import { NewsletterSignupModal } from '@/components/ui/newsletter-signup-modal'
import { LawnPrefillBanner, RelatedTools, ResultEmailCaptureCard } from '@/components/tools/shared'
import { useDefaultLawn } from '@/hooks/use-default-lawn'
import { useFromTool } from '@/hooks/use-from-tool'
import { parseGrassType, parseLawnSize, parseZipCode } from '@/hooks/useToolFromQuery'
import { analytics, stashEmailCaptureAttribution } from '@/lib/analytics'
import { buildToolUrl } from '@/lib/result-tool-urls'
import { guideSlugToGrassName } from '@/lib/grass-slug'
import { useCalculatorStarted } from '@/hooks/useCalculatorStarted'

export function SoilTestAnalyzerPageContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool('soil-test-analyzer')
  const { user, subscription } = useAuth()
  const { lawns, selectedLawnId, setSelectedLawnId, prefill: lawnPrefill } = useDefaultLawn()
  const [result, setResult] = useState<SoilTestSubmissionResult | null>(null)
  const {
    trackUsage,
    showNewsletterModal,
    closeNewsletterModal,
    usageCount,
  } = useNewsletterPrompt()
  const trackedUsageRef = useRef(false)
  // calculator_started on first input — this tool gates calculator_used on an
  // explicit report submit, so it can't see users who start entering but never
  // submit. The page body is the input container.
  const startedRef = useCalculatorStarted<HTMLElement>('soil-test-analyzer')
  const urlZip = parseZipCode(searchParams.get('zip') ?? '')
  const urlGrass = parseGrassType(searchParams.get('grass') ?? '')
  const urlLawnSize = parseLawnSize(searchParams.get('size') ?? '')

  useEffect(() => {
    if (!result || trackedUsageRef.current) return
    trackedUsageRef.current = true
    trackUsage('soil-test-analyzer')
    analytics.calculatorUsed('soil-test-analyzer', {
      inputs: {
        lab: result.values.lab,
        ph: result.values.ph ?? undefined,
        phosphorus_ppm: result.values.phosphorusPpm ?? undefined,
        potassium_ppm: result.values.potassiumPpm ?? undefined,
        organic_matter_pct: result.values.organicMatterPct ?? undefined,
        cec: result.values.cec ?? undefined,
      },
      result: {
        parsing_confidence: result.values.parsingConfidence,
        degraded: result.interpretation.degraded,
      },
      source: 'form_submit',
    })
  }, [result, trackUsage])

  return (
    <section ref={startedRef} className={result ? 'bg-white' : 'bg-gradient-to-b from-green-50 to-white'}>
      <div className={result ? 'max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-7 py-6' : 'container mx-auto px-4 py-8 max-w-4xl'}>
        <BreadcrumbComponent
          items={[
            { label: 'Tools', href: '/tools' },
            { label: 'Soil test analyzer' },
          ]}
        />

        {/* Marketing hero only shows pre-result so the verdict hero owns
            the top of the page once results land. */}
        {!result && (
          <header className="text-center mt-2 mb-7 sm:mb-8">
            <span className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.1em] text-green-700 mb-3.5">
              <span className="h-1.5 w-1.5 rounded-full bg-green-600" aria-hidden />
              Free tool · no signup
            </span>
            <h1 className="text-[clamp(2.25rem,4.6vw,3.375rem)] leading-[1.05] font-extrabold tracking-[-0.03em] text-slate-900 text-balance m-0">
              Turn your soil test into an{' '}
              <span className="text-green-600 border-b-4 border-green-600 pb-0.5">action plan</span>
            </h1>
            <p className="mt-4 text-[17px] leading-[1.55] text-gray-500 max-w-[600px] mx-auto">
              Upload any US lawn soil test PDF. Get plain-English fixes with products and rates sized to your lawn.
            </p>
          </header>
        )}

        <div className="space-y-7">
          {!result && (
            <>
              <LawnPrefillBanner lawns={lawns} selectedLawnId={selectedLawnId} onSelect={setSelectedLawnId} />
              <SoilTestForm
                initialValues={{
                  grassType: urlGrass ?? lawnPrefill.grassType,
                  zipCode: urlZip ?? lawnPrefill.zipCode,
                  lawnSize: urlLawnSize ?? lawnPrefill.lawnSizeSqft,
                  lawnId: selectedLawnId ?? undefined,
                }}
                onSuccess={setResult}
              />
            </>
          )}

          {result && (
            <>
              <SoilTestResults
                values={result.values}
                interpretation={result.interpretation}
                analysisContext={result.analysisContext}
                soilTestId={result.soilTestId}
                claimToken={result.claimToken}
                isAuthenticated={!!user}
                isPremium={!!subscription?.isPremium}
                onReset={() => setResult(null)}
              />
              <ResultEmailCaptureCard
                source="tool_result_soil_test"
                toolDisplayName="soil test plan"
                subcopy="Reminders to recheck your numbers before each season."
                grassType={result.analysisContext.grassType}
                zipCode={result.analysisContext.zipCode}
                accountLead={{
                  redirectTo: buildToolUrl("soil-test-analyzer", {
                    grassType: result.analysisContext.grassType,
                    zip: result.analysisContext.zipCode,
                    lawnSize: result.analysisContext.lawnSize,
                  }),
                  headline: (() => {
                    const grassName = guideSlugToGrassName(result.analysisContext.grassType)
                    return grassName
                      ? `Save my soil test plan for ${grassName}`
                      : "Save my soil test plan"
                  })(),
                  subcopy: "Keep this soil test plan in a free account and track your lawn over time.",
                  dividerLabel: "or get your 12-month calendar by email",
                  onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_soil_test"),
                }}
                onCaptured={() => {
                  closeNewsletterModal()
                }}
              />
            </>
          )}

          <RelatedTools
            currentToolId="soil-test-analyzer"
            prefillValues={{
              zip: result?.analysisContext.zipCode ?? lawnPrefill.zipCode,
              grassType: result?.analysisContext.grassType ?? lawnPrefill.grassType,
              lawnSize: result?.analysisContext.lawnSize ?? lawnPrefill.lawnSizeSqft,
            }}
          />
        </div>
      </div>

      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Soil Test Analyzer"
        usageCount={usageCount}
        grassType={result?.analysisContext?.grassType}
        zipCode={result?.analysisContext?.zipCode}
      />
    </section>
  )
}

// (Education card content now renders server-side via ToolContentFooter; see
// data/tool-content/soil-test-analyzer.ts.)
